import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { OpportunityActions } from '@/components/jobs/OpportunityActions';
import { ExplanationChips } from '@/components/MatchingUI';
import { skillbridgeDisplay, skillbridgeSections } from '@/lib/skillbridge';
import { X, Heart, MapPin, Briefcase, DollarSign, Building2, ExternalLink, Loader2, ArrowUpRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Session cache of canonical job details so reopening a job during this visit is instant
// and makes zero extra network requests. Capped so a long map session can't grow it unbounded.
const _jobCache = new Map();
const _CACHE_MAX = 30;
function _cacheSet(id, data) {
  if (_jobCache.has(id)) _jobCache.delete(id);
  _jobCache.set(id, data);
  if (_jobCache.size > _CACHE_MAX) _jobCache.delete(_jobCache.keys().next().value);
}

// Absolute Match (0-100). Map pins may carry a 0-1 fraction; list carries 0-100.
function matchPct(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return Math.round(n * (n <= 1 ? 100 : 1));
}

function Section({ title, children, testid }) {
  return (
    <section className="mb-6" data-testid={testid}>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</h3>
      {children}
    </section>
  );
}

// Zillow-style opportunity preview. Desktop → right-side drawer (~460px); mobile → bottom sheet.
// The map stays visible/interactive underneath (no page-blocking dialog). Loads canonical
// GET /api/jobs/{id}; never triggers Match scoring. Reuses the same favorite + application
// behavior as the list and full detail page.
export default function OpportunityPreview({
  jobId, user, resume, matchScore, explanationTags, distanceMiles,
  isFavorite, onToggleFavorite, onClose, onOpenFull,
}) {
  const [job, setJob] = useState(() => _jobCache.get(jobId) || null);
  const [loading, setLoading] = useState(!_jobCache.get(jobId));
  const [entered, setEntered] = useState(false);
  // While the native Apply modal (rendered by OpportunityActions) is open, suspend this
  // drawer so it never visually competes/overlaps with the Apply experience (drawer is z-[60],
  // above the dialog overlay). Restored when the Apply modal closes.
  const [applyOpen, setApplyOpen] = useState(false);
  // Only on wide desktop is there room to show this 460px drawer beside a centered
  // application dialog. On mobile/tablet the drawer is fully hidden while Apply is open.
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1440px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1440px)');
    const on = (e) => setWide(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  // Enter transition (translate in from the right on desktop / up on mobile).
  useEffect(() => {
    const r = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(r);
  }, []);

  // Signal the global FeedbackLauncher (Report Issue) to hide while the preview is open,
  // so it never sits under the sticky action footer. Restored on close/unmount.
  useEffect(() => {
    document.body.classList.add('opp-preview-open');
    return () => document.body.classList.remove('opp-preview-open');
  }, []);

  const handleClose = useCallback(() => {
    setEntered(false);
    setTimeout(() => onClose?.(), 240);
  }, [onClose]);

  // Close on Escape (but not while the Apply modal is open — let the dialog handle it).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !applyOpen) handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose, applyOpen]);

  // Load canonical details for the selected job (cached per session).
  useEffect(() => {
    let cancelled = false;
    const cached = _jobCache.get(jobId);
    if (cached) { setJob(cached); setLoading(false); return; }
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/jobs/${jobId}`)
      .then((r) => { if (cancelled) return; _cacheSet(jobId, r.data); setJob(r.data); })
      .catch(() => { if (!cancelled) setJob(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId]);

  const isSb = job?.opportunity_type === 'skillbridge';
  const sb = isSb ? skillbridgeDisplay(job) : null;
  const sbSections = isSb ? skillbridgeSections(job) : null;
  const title = job ? (isSb ? sb.displayTitle : job.title) : 'Loading…';
  const org = isSb ? (sb.showProvider ? sb.provider : null) : (job?.employer_name || null);
  const pct = matchPct(matchScore);
  const tags = Array.isArray(explanationTags) ? explanationTags : [];

  const panelPos = entered
    ? 'translate-y-0 md:translate-x-0'
    : 'translate-y-full md:translate-y-0 md:translate-x-full';

  // SkillBridge apply fits beside the drawer only on wide desktop. Everything else
  // (native apply, or SkillBridge on mobile/tablet) fully hides the drawer while Apply
  // is open so the application dialog is the single foreground surface.
  const canSideBySide = isSb && wide;
  const hideForApply = applyOpen && !canSideBySide;

  return (
    <div className={`fixed inset-0 z-[60] pointer-events-none ${hideForApply ? 'hidden' : ''}`} data-testid="opportunity-preview">
      {/* Mobile-only scrim (map stays interactive on desktop — no scrim there). */}
      <div
        className="md:hidden absolute inset-0 bg-black/30 pointer-events-auto transition-opacity"
        style={{ opacity: entered && !applyOpen ? 1 : 0 }}
        onClick={handleClose}
        data-testid="opportunity-preview-scrim"
      />

      <div
        className={`pointer-events-auto absolute bg-white shadow-2xl flex flex-col
          inset-x-0 bottom-0 h-[90vh] rounded-t-2xl
          md:inset-y-0 md:right-0 md:left-auto md:h-full md:w-[460px] md:rounded-none md:rounded-l-2xl
          transition-transform duration-300 ease-out ${panelPos}`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        role="dialog"
        aria-label="Opportunity details"
        data-testid="opportunity-preview-panel"
      >
        {/* Sticky header */}
        <div className="flex-shrink-0 border-b border-slate-200 px-5 pt-4 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {isSb ? (
                <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[11px] font-semibold uppercase tracking-wide" data-testid="preview-badge-skillbridge">SkillBridge</span>
              ) : (
                <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-wide" data-testid="preview-badge-employment">Employment</span>
              )}
              {job?.remote && (
                <span className="px-2 py-0.5 rounded-md bg-green-100 text-green-800 text-[11px] font-semibold">Remote</span>
              )}
              {pct != null && (
                <span className="text-[11px] font-semibold text-brand bg-brand-light px-2 py-0.5 rounded-full tabular-nums" data-testid="preview-match-pill">Match {pct}</span>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {user && (
                <button
                  onClick={onToggleFavorite}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                  aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  data-testid="preview-favorite-btn"
                >
                  <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-slate-300 hover:text-slate-400'}`} />
                </button>
              )}
              <button
                onClick={handleClose}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500"
                aria-label="Close"
                data-testid="preview-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mt-2 leading-snug" data-testid="preview-title">{title}</h2>
          {org && (
            <div className="flex items-center gap-1.5 text-slate-700 font-medium mt-1 text-sm" data-testid="preview-org">
              <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="truncate">
                {isSb && <span className="text-slate-400 font-normal">Provider: </span>}{org}
              </span>
            </div>
          )}
          {job && (
            <div className="flex items-center gap-1.5 text-slate-500 text-sm mt-1">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{job.remote ? 'Remote' : (job.location || 'Location not specified')}</span>
            </div>
          )}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="preview-body">
          {loading ? (
            <div className="flex justify-center py-16" data-testid="preview-loading">
              <Loader2 className="w-7 h-7 animate-spin text-brand" />
            </div>
          ) : !job ? (
            <p className="text-slate-500 py-10 text-center">This opportunity could not be loaded.</p>
          ) : (
            <>
              {/* Key facts */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-6" data-testid="preview-key-facts">
                {(job.salary_min || job.salary_max) && (
                  <Fact icon={DollarSign} label="Salary" value={`${job.salary_min ? `$${(job.salary_min / 1000).toFixed(0)}k` : ''}${job.salary_min && job.salary_max ? '–' : ''}${job.salary_max ? `$${(job.salary_max / 1000).toFixed(0)}k` : ''}`} />
                )}
                <Fact icon={MapPin} label="Work arrangement" value={job.remote ? 'Remote' : 'On-site'} />
                {isSb && sb.showProgramType && (
                  <Fact icon={Briefcase} label="Program" value={sb.programType} />
                )}
                {!isSb && job.job_type && (
                  <Fact icon={Briefcase} label="Job type" value={job.job_type.replace('_', ' ')} />
                )}
                {!job.remote && distanceMiles != null && (
                  <Fact icon={MapPin} label="Distance" value={`${Math.round(distanceMiles)} mi from home`} />
                )}
              </div>

              {/* Match reasons (stored only — never computed here) */}
              {tags.length > 0 && (
                <Section title="Why this matches" testid="preview-match-reasons">
                  <ExplanationChips tags={tags} max={4} />
                </Section>
              )}

              {/* Full details */}
              {isSb ? (
                <div data-testid="preview-sb-sections">
                  {sbSections.about && (
                    <Section title="About this opportunity" testid="preview-sb-about">
                      <p className="text-slate-700 leading-relaxed whitespace-pre-line text-[15px]">{sbSections.about}</p>
                    </Section>
                  )}
                  {sbSections.respBlocks && sbSections.respBlocks.length > 0 && (
                    <Section title="What you'll do" testid="preview-sb-responsibilities">
                      <div className="space-y-2.5 text-slate-700 text-[15px]">
                        {sbSections.respBlocks.map((b, i) => {
                          if (b.type === 'heading') return <h4 key={i} className="text-sm font-semibold text-slate-900 mt-3">{b.text}</h4>;
                          if (b.type === 'list') return <ul key={i} className="list-disc pl-5 space-y-1">{b.items.map((it, j) => <li key={j}>{it}</li>)}</ul>;
                          return <p key={i} className="leading-relaxed">{b.text}</p>;
                        })}
                      </div>
                    </Section>
                  )}
                  {sbSections.eligibility && (
                    <Section title="Eligibility" testid="preview-sb-eligibility">
                      <p className="text-slate-700 leading-relaxed whitespace-pre-line text-[15px]">{sbSections.eligibility}</p>
                    </Section>
                  )}
                  {sbSections.targetMocs && (
                    <Section title="Target military occupations" testid="preview-sb-mocs">
                      <p className="text-slate-700 leading-relaxed text-[15px]">{sbSections.targetMocs}</p>
                    </Section>
                  )}
                  {sbSections.careerFields && sbSections.careerFields.length > 0 && (
                    <Section title="Career fields" testid="preview-sb-career-fields">
                      <div className="flex flex-wrap gap-2">
                        {sbSections.careerFields.map((cf, i) => (
                          <span key={i} className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-sm font-medium">{cf}</span>
                        ))}
                      </div>
                    </Section>
                  )}
                  {sbSections.program.length > 0 && (
                    <Section title="Program details" testid="preview-sb-program">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                        {sbSections.program.map((row) => (
                          <div key={row.key}>
                            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-400 mb-0.5">{row.label}</div>
                            <div className="text-slate-800 text-sm">{row.value}</div>
                          </div>
                        ))}
                      </div>
                    </Section>
                  )}
                  {sbSections.notes && (
                    <Section title="Additional notes" testid="preview-sb-notes">
                      <p className="text-slate-700 leading-relaxed whitespace-pre-line text-[15px]">{sbSections.notes}</p>
                    </Section>
                  )}
                </div>
              ) : (
                <>
                  {job.description && (
                    <Section title="About the role" testid="preview-description">
                      <p className="text-slate-700 whitespace-pre-wrap text-[15px] leading-relaxed">{job.description}</p>
                    </Section>
                  )}
                  {job.requirements && (Array.isArray(job.requirements) ? job.requirements.length > 0 : !!job.requirements) && (
                    <Section title="Requirements" testid="preview-requirements">
                      <p className="text-slate-700 whitespace-pre-wrap text-[15px] leading-relaxed">
                        {Array.isArray(job.requirements) ? job.requirements.join('\n') : job.requirements}
                      </p>
                    </Section>
                  )}
                  {job.source_type === 'external' && (
                    <Section title="Original posting" testid="preview-external-source">
                      <p className="text-sm text-slate-500">Source: {job.source_name || 'External'}</p>
                    </Section>
                  )}
                </>
              )}

              {/* Open full opportunity */}
              <button
                onClick={() => onOpenFull?.(jobId)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
                data-testid="preview-open-full-btn"
              >
                Open full opportunity <ArrowUpRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* Sticky action footer */}
        {job && (
          <div className="flex-shrink-0 border-t border-slate-200 bg-white px-5 py-3" data-testid="preview-actions">
            <OpportunityActions job={job} user={user} resume={resume} onApplyOpenChange={setApplyOpen} />
          </div>
        )}
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-slate-400 mb-0.5">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-slate-800 text-sm font-medium capitalize truncate">{value}</div>
    </div>
  );
}
